<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useSupabaseDashboard } from 'dashboard/composables/useSupabaseDashboard';
import { useCallControl } from 'dashboard/composables/useCallControl';
import contactsApi from 'dashboard/api/contacts';

// Períodos rápidos de filtro
const DATE_PERIODS = [
  { key: 'today', label: 'Hoje' },
  { key: '7days', label: 'Últimos 7 dias' },
  { key: 'this_month', label: 'Este mês' },
  { key: 'last_month', label: 'Último mês' },
];

function getPeriodRange(key) {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (key === 'today') {
    const today = fmt(now);
    return { dateFrom: `${today}T00:00:00-03:00`, dateTo: `${today}T23:59:59.999-03:00` };
  }
  if (key === '7days') {
    const from = new Date(now);
    from.setDate(from.getDate() - 6);
    return { dateFrom: `${fmt(from)}T00:00:00-03:00`, dateTo: `${fmt(now)}T23:59:59.999-03:00` };
  }
  if (key === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateFrom: `${fmt(from)}T00:00:00-03:00`, dateTo: `${fmt(now)}T23:59:59.999-03:00` };
  }
  if (key === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return { dateFrom: `${fmt(from)}T00:00:00-03:00`, dateTo: `${fmt(to)}T23:59:59.999-03:00` };
  }
  return {};
}

const store = useStore();
const router = useRouter();

// Usuário logado e sua role (auth não é namespaced — getters globais)
const currentUserId = computed(() => store.getters['getCurrentUserID']);
const { isAdmin } = useAdmin();

// Lista de agentes (usada pelo filtro do admin; agents é namespaced)
const agents = computed(() => store.getters['agents/getAgents'] || []);

// ✅ Supabase Dashboard - Dados persistentes do banco de dados
const {
  callHistory: callHistoryFromSupabase,
  isLoading,
  error: supabaseError,
  refresh
} = useSupabaseDashboard();

// Período selecionado — padrão: hoje
const selectedPeriod = ref('today');

const selectPeriod = async (key) => {
  selectedPeriod.value = key;
  await refresh(getPeriodRange(key));
};

// ✅ Call Control - Controle de conexão WebSocket e dados de fila
const { isConnected, inQueue } = useCallControl();

// Mapeamento de logos das unidades. O unit vem do Asterisk e a grafia varia,
// então casamos por trecho do nome (e pelo número, no caso da Altana).
// A Altana vem primeiro: o nome dela contém "Lush" (by Lush Hospitality Group).
const unitLogos = [
  { match: ['altana', '6131424299'], logo: '/brands/altana/logos/logo-light.png' },
  { match: ['tout'], logo: '/brands/tout/logos/logo-main.png' },
  { match: ['lush'], logo: '/brands/lush/logos/logo-main.png' },
  { match: ['andar de cima', 'andar-de-cima'], logo: '/brands/andar-de-cima/logos/logo-main.png' },
];

// Função para pegar a logo da unidade
const getUnitLogo = (unitName) => {
  const name = (unitName || '').toLowerCase();
  const unit = unitLogos.find(u => u.match.some(m => name.includes(m)));
  return unit ? unit.logo : null;
};

// Histórico bruto do Supabase
const callHistory = computed(() => callHistoryFromSupabase.value || []);

// Mapa de enriquecimento: número → nome do contato no Chatwoot
const contactNamesMap = ref({});

const enrichWithContactNames = async (calls) => {
  const uniqueNumbers = [...new Set(calls.map(c => c.callerNumber).filter(Boolean))];
  const newNumbers = uniqueNumbers.filter(n => !(n in contactNamesMap.value));
  if (!newNumbers.length) return;

  const results = await Promise.allSettled(
    newNumbers.map(number => contactsApi.search(number))
  );

  const updates = {};
  results.forEach((result, i) => {
    const number = newNumbers[i];
    if (result.status === 'fulfilled') {
      const contacts = result.value?.data?.payload || [];
      const match = contacts.find(c =>
        c.phone_number && c.phone_number.replace(/\D/g, '').endsWith(number.replace(/\D/g, ''))
      );
      updates[number] = match ? { name: match.name, id: match.id } : null;
    }
  });
  contactNamesMap.value = { ...contactNamesMap.value, ...updates };
};

watch(callHistory, (calls) => {
  if (calls.length) enrichWithContactNames(calls);
}, { immediate: true });

const goToContact = (callerNumber) => {
  const contact = contactNamesMap.value[callerNumber];
  if (!contact?.id) return;
  const accountId = store.getters['getCurrentAccountId'];
  router.push(`/app/accounts/${accountId}/contacts/${contact.id}`);
};

// Filtros
const filters = ref({
  searchTerm: '',
  status: '',    // 'answered', 'missed' ou '' (todos)
  agentId: '',   // ID do agente selecionado — apenas admin
  dateFrom: '',
  dateTo: '',
});

// Ao preencher data manual, desmarca o período rápido e refaz query no Supabase
const applyCustomDate = async () => {
  if (!filters.value.dateFrom && !filters.value.dateTo) return;
  selectedPeriod.value = '';
  await refresh({
    dateFrom: filters.value.dateFrom ? `${filters.value.dateFrom}T00:00:00-03:00` : null,
    dateTo: filters.value.dateTo ? `${filters.value.dateTo}T23:59:59.999-03:00` : null,
  });
};

// ID do agente que serve de base para TODOS os dados do dashboard:
// - Agent: sempre o próprio ID
// - Admin: agente selecionado no dropdown ('' = visão geral de todos)
const activeAgentId = computed(() => {
  if (!isAdmin.value) return String(currentUserId.value);
  return filters.value.agentId;
});

// Histórico base filtrado por role/agente — alimenta bignumbers, gráficos e tabela
const roleFilteredHistory = computed(() => {
  const agentId = activeAgentId.value;
  if (!agentId) return callHistory.value; // admin sem agente selecionado = todos
  return callHistory.value.filter(call =>
    call.status === 'missed' ||
    (call.status === 'answered' && String(call.agentId) === agentId)
  );
});

// Bignumbers calculados a partir do histórico filtrado por role/agente
const callsStats = computed(() => {
  const base = roleFilteredHistory.value;
  const answered = base.filter(c => c.status === 'answered').length;
  const missed = base.filter(c => c.status === 'missed').length;
  const answeredWithDuration = base.filter(c => c.status === 'answered' && c.duration > 0);
  const avgDuration = answeredWithDuration.length > 0
    ? Math.round(answeredWithDuration.reduce((sum, c) => sum + c.duration, 0) / answeredWithDuration.length)
    : 0;
  return { answered, missed, received: answered + missed, avgDuration, inQueue: inQueue.value || 0 };
});

// Gráfico por unidade calculado a partir do histórico filtrado por role/agente
const callsByUnit = computed(() => {
  const unitMap = new Map();
  roleFilteredHistory.value.forEach(call => {
    const unit = call.unit || 'Telefone';
    if (!unitMap.has(unit)) unitMap.set(unit, { unit, answered: 0, missed: 0, total: 0 });
    const stats = unitMap.get(unit);
    stats.total++;
    if (call.status === 'answered') stats.answered++;
    else if (call.status === 'missed') stats.missed++;
  });
  return Array.from(unitMap.values()).sort((a, b) => b.total - a.total);
});

// Histórico filtrado para a tabela (status + busca + datas aplicados sobre roleFilteredHistory)
const filteredHistory = computed(() => {
  let filtered = roleFilteredHistory.value;

  if (filters.value.status) {
    filtered = filtered.filter(call => call.status === filters.value.status);
  }

  if (filters.value.searchTerm) {
    const term = filters.value.searchTerm.toLowerCase();
    filtered = filtered.filter(call =>
      call.callerName.toLowerCase().includes(term) ||
      call.callerNumber.includes(term) ||
      call.unit.toLowerCase().includes(term)
    );
  }

  return filtered;
});

// Paginação
const currentPage = ref(1);
const perPage = 10;
const totalRecords = computed(() => filteredHistory.value.length);

const paginatedHistory = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  const end = start + perPage;
  return filteredHistory.value.slice(start, end);
});

const totalPages = computed(() => Math.ceil(totalRecords.value / perPage));

// Resetar para página 1 quando os filtros mudarem
const resetPage = () => {
  currentPage.value = 1;
};

// Watch para mudanças nos filtros
watch(filters, () => {
  resetPage();
}, { deep: true });

// Limpar filtros
const clearFilters = async () => {
  filters.value = {
    searchTerm: '',
    status: '',
    agentId: '',
    dateFrom: '',
    dateTo: '',
  };
  selectedPeriod.value = 'today';
  await refresh(getPeriodRange('today'));
};

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTime(isoString) {
  const date = new Date(isoString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(isoString) {
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR');
}

function formatTimeRange(isoString, durationSeconds) {
  const startDate = new Date(isoString);
  const startTime = startDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // Se não tiver duração ou for 0 (chamada perdida), retorna só a hora inicial
  if (!durationSeconds || durationSeconds === 0) {
    return startTime;
  }

  // Calcular hora final
  const endDate = new Date(startDate.getTime() + durationSeconds * 1000);
  const endTime = endDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${startTime} - ${endTime}`;
}

const getAgentName = (agentId) => {
  if (!agentId) return '—';
  const agent = agents.value.find(a => String(a.id) === String(agentId));
  return agent ? agent.name : '—';
};

const getStatusColor = status => {
  switch (status) {
    case 'answered':
      return 'green';
    case 'missed':
      return 'red';
    default:
      return 'gray';
  }
};

const getStatusText = status => {
  switch (status) {
    case 'answered':
      return 'Atendida';
    case 'missed':
      return 'Perdida';
    default:
      return status;
  }
};

// ✅ Carregar dados do Supabase
const loadHistory = async () => {
  await refresh();
};

onMounted(async () => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  if (isAdmin.value) {
    store.dispatch('agents/get');
  }
  // Carregar dados do período padrão (hoje)
  await refresh(getPeriodRange('today'));
});
</script>

<template>
  <div class="call-control-dashboard">
    <!-- Header Compact -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-text">
            <h1>Dashboard de Chamadas</h1>
            <span>Estatísticas e histórico de atendimentos</span>
          </div>
        </div>
        <div class="header-right">
          <div class="period-selector">
            <button
              v-for="period in DATE_PERIODS"
              :key="period.key"
              class="period-btn"
              :class="{ 'period-btn--active': selectedPeriod === period.key }"
              @click="selectPeriod(period.key)"
            >
              {{ period.label }}
            </button>
          </div>
          <div
            class="connection-badge"
            :class="{ connected: isConnected, disconnected: !isConnected }"
          >
            <span class="status-dot" />
            <span class="status-label">{{
              isConnected ? 'Conectado' : 'Desconectado'
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content - Flexbox Layout -->
    <div class="dashboard-content">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon stat-icon--green">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
              />
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ callsStats.answered }}</div>
            <div class="stat-label">{{
              'Atendidas'
            }}</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon stat-icon--red">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ callsStats.missed }}</div>
            <div class="stat-label">Perdidas</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon stat-icon--blue">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
              />
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ callsStats.received }}</div>
            <div class="stat-label">{{
              'Recebidas'
            }}</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon stat-icon--orange">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 22h14" />
              <path d="M5 2h14" />
              <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
              <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ callsStats.inQueue }}</div>
            <div class="stat-label">Em fila</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon stat-icon--purple">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{
              formatDuration(callsStats.avgDuration)
            }}</div>
            <div class="stat-label">{{
              'Tempo Médio'
            }}</div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <div class="chart-card">
          <div class="card-header">
            <h3>Ligações por Unidade</h3>
          </div>
          <div class="chart-body">
            <div class="unit-charts">
              <div
                v-for="item in callsByUnit"
                :key="item.unit"
                class="unit-chart-item"
              >
                <div class="unit-info">
                  <img
                    v-if="getUnitLogo(item.unit)"
                    :src="getUnitLogo(item.unit)"
                    :alt="item.unit"
                    class="unit-logo"
                  />
                  <span v-else class="unit-name">{{ item.unit }}</span>
                  <span class="unit-total">{{ item.total }}</span>
                </div>
                <div class="unit-bar">
                  <div
                    class="unit-bar-fill unit-bar--answered"
                    :style="{ width: (item.answered / item.total) * 100 + '%' }"
                  />
                  <div
                    class="unit-bar-fill unit-bar--missed"
                    :style="{ width: (item.missed / item.total) * 100 + '%' }"
                  />
                </div>
                <div class="unit-legend">
                  <span class="legend-item legend-item--green">
                    <span class="legend-dot" />
                    Atendidas: {{ item.answered }}
                  </span>
                  <span class="legend-item legend-item--red">
                    <span class="legend-dot" />
                    Perdidas: {{ item.missed }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="card-header">
            <h3>Perdidas vs Atendidas</h3>
          </div>
          <div class="chart-body chart-body--center">
            <div class="donut-wrapper">
              <svg viewBox="0 0 100 100" class="donut-chart">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#374151"
                  stroke-width="12"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  :stroke-dasharray="`${callsStats.answered * 20.1} ${314 - callsStats.answered * 20.1}`"
                  stroke="#22c55e"
                  stroke-width="12"
                  transform="rotate(-90 50 50)"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  :stroke-dasharray="`${callsStats.missed * 20.1} ${314 - callsStats.missed * 20.1}`"
                  stroke="#ef4444"
                  stroke-width="12"
                  :stroke-dashoffset="`-${callsStats.answered * 20.1}`"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div class="donut-center">
                <div class="donut-total">{{ callsStats.received }}</div>
                <div class="donut-label">total</div>
              </div>
            </div>
            <div class="donut-legend">
              <div class="legend-row">
                <span class="legend-dot legend-dot--green" />
                <span>Atendidas</span>
                <strong>{{ callsStats.answered }}</strong>
              </div>
              <div class="legend-row">
                <span class="legend-dot legend-dot--red" />
                <span>Perdidas</span>
                <strong>{{ callsStats.missed }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- History Table -->
      <div class="history-section">
        <div class="history-card">
          <div class="card-header">
            <h3>Histórico de Chamadas</h3>
          </div>
          <div class="history-body">
            <!-- Filtros -->
            <div class="filters-section">
              <div class="filter-row">
                <div class="filter-group filter-group--search">
                  <input
                    v-model="filters.searchTerm"
                    type="text"
                    placeholder="     Buscar por nome, número ou unidade..."
                    class="filter-input filter-input--search"
                  />
                </div>
                <div v-if="isAdmin" class="filter-group">
                  <select v-model="filters.agentId" class="filter-select">
                    <option value="">Todos os agentes</option>
                    <option
                      v-for="agent in agents"
                      :key="agent.id"
                      :value="String(agent.id)"
                    >
                      {{ agent.name }}
                    </option>
                  </select>
                </div>
                <div class="filter-group">
                  <select v-model="filters.status" class="filter-select">
                    <option value="">Todos os status</option>
                    <option value="answered">Atendidas</option>
                    <option value="missed">Perdidas</option>
                  </select>
                </div>
                <div class="filter-group filter-group--date">
                  <label class="filter-label">Data Inicial</label>
                  <div class="date-input-wrapper">
                    <input
                      v-model="filters.dateFrom"
                      type="date"
                      class="filter-input"
                      @click="$event.target.showPicker()"
                      @change="applyCustomDate"
                    />
                  </div>
                </div>
                <div class="filter-group filter-group--date">
                  <label class="filter-label">Data Final</label>
                  <div class="date-input-wrapper">
                    <input
                      v-model="filters.dateTo"
                      type="date"
                      class="filter-input"
                      @click="$event.target.showPicker()"
                      @change="applyCustomDate"
                    />
                  </div>
                </div>
                <button @click="clearFilters" class="btn-clear-filters">
                  Limpar Filtros
                </button>
              </div>
            </div>

            <table class="history-table">
              <thead>
                <tr>
                  <th>Contato</th>
                  <th>Unidade</th>
                  <th v-if="isAdmin">Agente</th>
                  <th>Status</th>
                  <th>Duração</th>
                  <th>Data/Hora</th>
                  <th>Gravação da Chamada</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="call in paginatedHistory"
                  :key="call.id"
                  class="history-row"
                  :class="{ 'history-row--missed': call.status === 'missed' }"
                >
                  <td
                    class="caller-cell"
                    :class="{ 'caller-cell--clickable': contactNamesMap[call.callerNumber]?.id }"
                    :title="contactNamesMap[call.callerNumber] === null ? 'Contato não cadastrado' : undefined"
                    @click="goToContact(call.callerNumber)"
                  >
                    <div class="caller-number">{{ contactNamesMap[call.callerNumber]?.name || call.callerNumber }}</div>
                    <div class="caller-name">{{ call.callerNumber }}</div>
                  </td>
                  <td>
                    <img
                      v-if="getUnitLogo(call.unit)"
                      :src="getUnitLogo(call.unit)"
                      :alt="call.unit"
                      class="unit-logo"
                      :title="call.unit"
                    />
                    <span v-else class="unit-badge">{{ call.unit }}</span>
                  </td>
                  <td v-if="isAdmin" class="agent-cell">
                    {{ getAgentName(call.agentId) }}
                  </td>
                  <td>
                    <span
                      class="status-badge"
                      :class="`status-badge--${getStatusColor(call.status)}`"
                    >
                      {{ getStatusText(call.status) }}
                    </span>
                  </td>
                  <td>
                    <span class="duration-text">{{
                      formatDuration(call.duration)
                    }}</span>
                  </td>
                  <td>
                    <div class="time-cell">
                      <div class="time-date">{{ formatDate(call.timestamp) }}</div>
                      <div class="time-time">{{ formatTimeRange(call.timestamp, call.duration) }}</div>
                    </div>
                  </td>
                  <td class="recording-cell">
                    <a
                      v-if="call.recordingUrl"
                      :href="call.recordingUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="download-link"
                      title="Ouvir gravação"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M3 18v-6a9 9 0 0 1 18 0v6"
                        />
                        <path
                          d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"
                        />
                      </svg>
                      Ouvir
                    </a>
                    <span v-else class="no-recording">-</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="totalPages > 1" class="pagination">
              <button
                class="pagination-btn"
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="15 19 9 12 15 5" />
                </svg>
              </button>
              <span class="pagination-text"
                >Página {{ currentPage }}
                de {{ totalPages }}</span
              >
              <button
                class="pagination-btn"
                :disabled="currentPage === totalPages"
                @click="currentPage++"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="9 19 15 12 9 5" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.call-control-dashboard {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #121213;
}

/* Header */
.dashboard-header {
  width: 100%;
  background: #17171a;
  border-bottom: 1px solid #333333;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
  box-sizing: border-box;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.period-selector {
  display: flex;
  gap: 0.25rem;
  background: #0e0e10;
  border: 1px solid #333333;
  border-radius: 8px;
  padding: 0.25rem;
}

.period-btn {
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.period-btn:hover {
  background: #27272a;
  color: #e5e7eb;
}

.period-btn--active {
  background: #27272a;
  color: #f9fafb;
}

.header-icon {
  color: #8b5cf6;
  flex-shrink: 0;
}

.header-text h1 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f9fafb;
  margin: 0;
  line-height: 1.2;
}

.header-text span {
  font-size: 0.8125rem;
  color: #9ca3af;
}

.connection-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.connection-badge.connected {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.connection-badge.disconnected {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse-dot 2s infinite;
}

.connection-badge.disconnected .status-dot {
  animation: none;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Main Content - Scrollable */
.dashboard-content {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-sizing: border-box;
}

/* Stats Row - Flexbox */
.stats-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
}

.stat-card {
  background: #17171a;
  border: 1px solid #333333;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1 1 0;
  min-width: 200px;
  box-sizing: border-box;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon--green {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
}

.stat-icon--red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.stat-icon--blue {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.stat-icon--purple {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.stat-icon--orange {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 1.625rem;
  font-weight: 700;
  color: #f9fafb;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.125rem;
}

/* Charts Section - Flexbox */
.charts-section {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  width: 100%;
}

.chart-card {
  background: #17171a;
  border: 1px solid #333333;
  border-radius: 10px;
  overflow: hidden;
  flex: 1 1 0;
  min-width: 300px;
  box-sizing: border-box;
}

.card-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #333333;
}

.card-header h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #f9fafb;
  margin: 0;
}

.chart-body {
  padding: 1.25rem;
}

.chart-body--center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Unit Charts */
.unit-charts {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.unit-chart-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.unit-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.unit-info .unit-logo {
  width: 50px;
  height: 50px;
}

.unit-info .unit-logo[alt="Andar de Cima"] {
  width: 70px;
  height: 70px;
}

/* Versão lilás da logo (logo-light.png) para o fundo escuro do dashboard; a
   logo original é verde escura e sumiria aqui */
.unit-info .unit-logo[alt*="ALTANA" i] {
  width: 70px;
  height: auto;
}

.unit-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #e5e7eb;
}

.unit-total {
  font-size: 0.875rem;
  font-weight: 700;
  color: #f9fafb;
}

.unit-bar {
  height: 8px;
  background: #374151;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
}

.unit-bar-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.unit-bar--answered {
  background: #22c55e;
}

.unit-bar--missed {
  background: #ef4444;
}

.unit-legend {
  display: flex;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #9ca3af;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.legend-dot--green {
  background: #22c55e;
}

.legend-dot--red {
  background: #ef4444;
}

/* Donut Chart */
.donut-wrapper {
  position: relative;
  width: 180px;
  height: 180px;
}

.donut-chart {
  width: 100%;
  height: 100%;
}

.donut-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.donut-total {
  font-size: 2rem;
  font-weight: 700;
  color: #f9fafb;
  line-height: 1;
}

.donut-label {
  font-size: 0.6875rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
  margin-top: 1rem;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #e5e7eb;
}

.legend-row strong {
  margin-left: auto;
  font-weight: 600;
}

/* History Section */
.history-section {
  display: flex;
  width: 100%;
}

.history-card {
  background: #17171a;
  border: 1px solid #333333;
  border-radius: 10px;
  overflow: hidden;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
}

.history-body {
  padding: 0;
}

/* Filters Section */
.filters-section {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #333333;
  background: #0d0d0e;
}

.filter-row {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  flex: 1;
  min-width: 150px;
}

.filter-group--search {
  flex: 2;
  min-width: 250px;
}

.filter-group--date {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
}

.filter-group--date input {
  cursor: pointer;
}

.filter-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.filter-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: #17171a;
  border: 1px solid #333333;
  border-radius: 6px;
  color: #e5e7eb;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.15s ease;
}

.filter-input:focus {
  border-color: #8b5cf6;
}

.filter-input--search {
  padding-left: 3.5rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.35-4.35'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 0.75rem center;
  transition: all 0.2s ease;
}

.filter-input--search:focus {
  background-image: none;
  padding-left: 0.75rem;
}

.filter-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: #17171a;
  border: 1px solid #333333;
  border-radius: 6px;
  color: #e5e7eb;
  font-size: 0.8125rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.filter-select:focus {
  border-color: #8b5cf6;
}

.btn-clear-filters {
  height: calc(1.75rem + 1.25rem + 0.25rem); /* Altura do label + input + gap */
  padding: 0.5rem 1rem;
  background: #374151;
  border: 1px solid #4b5563;
  border-radius: 6px;
  color: #e5e7eb;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  align-self: center;
}

.btn-clear-filters:hover {
  background: #4b5563;
  border-color: #6b7280;
}

/* History Table */
.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table thead {
  background: #0d0d0e;
  border-bottom: 1px solid #333333;
}

.history-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-table th:last-child {
  width: 140px;
  text-align: center;
}

.history-table th:nth-last-child(2) {
  text-align: center;
}

.history-row {
  border-bottom: 1px solid #2a2a2a;
  transition: background 0.15s ease;
}

.history-row:hover {
  background: #1a1a1b;
}

.history-row--missed {
  background: rgba(239, 68, 68, 0.05);
}

.history-row--missed:hover {
  background: rgba(239, 68, 68, 0.08);
}

.history-row td {
  padding: 0.875rem 1rem;
}

.caller-cell {
  min-width: 160px;
}

.caller-cell--clickable {
  cursor: pointer;
}

.caller-cell--clickable:hover .caller-number {
  text-decoration: underline;
}

.caller-number {
  font-size: 0.875rem;
  font-weight: 600;
  color: #f9fafb;
}

.caller-name {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.125rem;
}

.unit-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #6366f1;
  color: white;
  font-size: 0.6875rem;
  font-weight: 500;
  border-radius: 4px;
}

.unit-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border-radius: 4px;
}

.unit-logo[alt*="ALTANA" i] {
  width: 80px;
  height: auto;
}

.duration-text {
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-family: 'SF Mono', 'Monaco', 'Cascadia Code', monospace;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 4px;
}

.status-badge--green {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge--red {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.time-cell {
  text-align: center;
}

.time-date {
  font-size: 0.75rem;
  color: #9ca3af;
}

.time-time {
  font-size: 0.8125rem;
  color: #f9fafb;
  font-weight: 500;
}

/* Recording Cell */
.recording-cell {
  text-align: center;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #8b5cf6;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  background: rgba(139, 92, 246, 0.1);
  transition: all 0.15s ease;
}

.download-link:hover {
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
}

.no-recording {
  color: #6b7280;
  font-size: 0.8125rem;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid #333333;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #333333;
  border-radius: 6px;
  background: #242424;
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.15s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #8b5cf6;
  border-color: #8b5cf6;
  color: white;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-section {
    flex-direction: column;
  }

  .chart-card {
    min-width: 100%;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    padding: 1rem;
  }

  .stats-row {
    flex-direction: column;
  }

  .stat-card {
    min-width: 100%;
  }

  .history-table {
    font-size: 0.8125rem;
  }

  .history-table th,
  .history-table td {
    padding: 0.625rem 0.5rem;
  }

  .caller-cell {
    min-width: 120px;
  }
}

/* Scrollbar Styling */
.dashboard-content::-webkit-scrollbar {
  width: 8px;
}

.dashboard-content::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.dashboard-content::-webkit-scrollbar-thumb {
  background: #333333;
  border-radius: 4px;
}

.dashboard-content::-webkit-scrollbar-thumb:hover {
  background: #404040;
}
</style>
